import { useEffect, useMemo, useRef, useState } from "react";
import { useSceneStore } from "../scene/SceneHost.js";
import { onWs } from "../api/ws.js";
import { useT } from "../i18n/index.js";
import { Button } from "../ui/Button.js";
import { ConstructionSummary } from "./ConstructionSummary.js";
import { BaseScene } from "./BaseScene.js";
import { Labels } from "./Labels.js";
import { placementValid, useBase } from "./state.js";
import { rotated } from "./grid.js";
import { BuildingPanel } from "./panels/BuildingPanel.js";
import { BuildMenu, typeFits } from "./panels/BuildMenu.js";
import { buildCatalogue, readSeen } from "./panels/buildCatalogue.js";
import { useBalances } from "../state/balances.js";
import { useSession } from "../state/session.js";
import { HqPanel } from "./panels/HqPanel.js";
import { QueuePanel } from "./panels/QueuePanel.js";
import { ZonePanel } from "./panels/ZonePanel.js";
import { ProtocolPanel } from "./panels/ProtocolPanel.js";
import { ResearchPanel } from "./panels/ResearchPanel.js";
import { LayoutPanel } from "./panels/LayoutPanel.js";
import { DecorationSheet } from "./panels/DecorationSheet.js";
import { decorationById } from "@zend/content";
import { pick } from "../i18n/index.js";
import { ColonyActivity, CollectionFlights } from "./ColonyPanels.js";
import { buildingName } from "./panels/shared.js";
import { useStore } from "../store/state.js";
import { retryTerrace, setTerraceSkin, subscribeTerraceState, type TerraceState } from "../scene/baseTheme.js";
import { Icon } from "../ui/Icon.js";

/**
 * The base screen: wires the store to the 3D scene, and renders chips, labels, the build FAB, the placement bar
 * and whichever panel is open. Stays mounted under every route so the base remains visible (20-base.md).
 */
export function BaseView({ active, hidden = false }: { active: boolean; hidden?: boolean }) {
  const t = useT();
  const handle = useSceneStore((s) => s.handle);
  const baseSkin = useStore((s) => s.cosmetics?.find((cosmetic) => cosmetic.kind === "base_skin" && cosmetic.equipped)?.id ?? null);
  const [scene, setScene] = useState<BaseScene | null>(null);
  const [surface, setSurface] = useState<TerraceState | null>(null);
  const sceneRef = useRef<BaseScene | null>(null);
  const base = useBase((s) => s.base);
  const mode = useBase((s) => s.mode);
  const panel = useBase((s) => s.panel);
  const ghost = useBase((s) => s.ghost);
  const placing = useBase((s) => s.placing);
  const balances = useBalances((s) => s.balances);
  const accountId = useSession((s) => s.account?.id ?? "anon");
  // The FAB badge counts types that are unlocked, affordable or not, and actually have room (D65).
  const catalogue = useMemo(() => (base ? buildCatalogue(base, balances, readSeen(accountId), (id) => typeFits(base, id)) : []), [base, balances, accountId]);
  const readyCount = catalogue.filter((e) => e.group === "ready" && e.fits).length;
  const readyNew = catalogue.some((e) => e.isNew && e.fits);
  const decoOwned = base?.loop?.decorationStock?.owned.reduce((n, o) => n + o.qty, 0) ?? 0;
  const celebrate = useBase((s) => s.celebrate);
  const focus = useBase((s) => s.focus);
  const refresh = useBase((s) => s.refresh);

  useEffect(() => {
    const terrace = handle?.scene.getObjectByName("enclave.terrace");
    if (terrace) setTerraceSkin(terrace, baseSkin);
  }, [handle, baseSkin]);

  useEffect(() => {
    const terrace = handle?.scene.getObjectByName("enclave.terrace");
    if (!terrace) { setSurface(null); return; }
    return subscribeTerraceState(terrace, setSurface);
  }, [handle]);

  useEffect(() => {
    if (!handle) return;
    const s = new BaseScene(handle, {
      onTap: (rowId) => {
        const st = useBase.getState();
        if (st.mode.kind !== "view") return;
        if (rowId?.startsWith("deco:")) st.setPanel({ kind: "decoration", id: rowId.slice(5) });
        else if (rowId) st.select(rowId);
        else if (st.panel.kind !== "none") st.setPanel({ kind: "none" });
      },
      onGhostMove: (x, y) => {
        const st = useBase.getState();
        if (st.mode.kind === "view" || !st.base || st.placing) return;
        const valid = placementValid(st.base, x, y, st.mode.w, st.mode.h, st.mode.kind === "move" ? st.mode.rowId : undefined, st.mode.kind === "moveDecoration" ? st.mode.id : undefined);
        st.setGhost({ x, y, valid });
      },
    });
    sceneRef.current = s;
    setScene(s);
    return () => {
      s.dispose();
      sceneRef.current = null;
      setScene(null);
    };
  }, [handle]);

  useEffect(() => {
    if (!useBase.getState().base) void refresh();
    const offBuilding = onWs("base.building", (e) => useBase.getState().onBuildingPush(e.payload as { rowId: string; buildingId: string; level: number; state: string }));
    const offZone = onWs("base.zone", () => void useBase.getState().refresh());
    const offResearch = onWs("research.node", () => {
      void useBase.getState().refresh();
      if (useBase.getState().panel.kind === "research") void useBase.getState().refreshResearch();
    });
    const poll = setInterval(() => void useBase.getState().refresh(), 45_000);
    const resume = () => { if (document.visibilityState === "visible") void useBase.getState().refresh(); };
    document.addEventListener("visibilitychange", resume);
    return () => {
      offBuilding();
      offZone();
      offResearch();
      clearInterval(poll);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [refresh]);

  useEffect(() => sceneRef.current?.setInteractive(active), [active, scene]);
  useEffect(() => sceneRef.current?.setVisible(!hidden), [hidden, scene]);
  useEffect(() => {
    if (base) sceneRef.current?.update(base);
  }, [base, scene]);
  useEffect(() => sceneRef.current?.setSelection(active && panel.kind === "building" ? panel.rowId : null), [panel, active, scene]);
  useEffect(() => {
    if (mode.kind === "view" || !ghost) sceneRef.current?.setGhost(null);
    else sceneRef.current?.setGhost({ ...ghost, w: mode.w, h: mode.h, rot: mode.rot, ...(mode.kind === "move" ? { rowId: mode.rowId } : {}) });
  }, [mode, ghost, scene]);
  useEffect(() => {
    if (!focus) return;
    sceneRef.current?.focus(focus);
    useBase.getState().setFocus(null);
  }, [focus, scene]);
  useEffect(() => {
    if (!celebrate) return;
    sceneRef.current?.celebrate(celebrate);
    useBase.getState().clearCelebrate();
  }, [celebrate, scene]);
  useEffect(() => {
    if (!active) useBase.getState().cancelMode();
  }, [active]);

  if (!active || !base) return null;

  const rotate = () => {
    const st = useBase.getState();
    if (st.mode.kind === "view" || !st.base || !st.ghost) return;
    const rot = (st.mode.rot + 90) % 360;
    const fp = rotated(st.mode.w, st.mode.h, 90);
    const next = { ...st.mode, rot, w: fp.w, h: fp.h };
    const valid = placementValid(st.base, st.ghost.x, st.ghost.y, fp.w, fp.h, st.mode.kind === "move" ? st.mode.rowId : undefined, st.mode.kind === "moveDecoration" ? st.mode.id : undefined);
    st.setMode(next);
    st.setGhost({ x: st.ghost.x, y: st.ghost.y, valid });
  };

  const modeName = mode.kind === "place" ? buildingName(mode.buildingId) : mode.kind === "move" ? buildingName(base.buildings.find((b) => b.id === mode.rowId)?.buildingId ?? "bld.command_nexus") : mode.kind === "decorate" || mode.kind === "moveDecoration" ? pick(decorationById(mode.decoId).name) : "";

  return (
    <>
      {mode.kind === "view" ? <Labels scene={scene} /> : null}
      {panel.kind === "none" && mode.kind === "view" ? <ColonyActivity /> : null}
      <CollectionFlights />
      {(surface?.status === "loading" || surface?.status === "error") && !hidden ? (
        <div className="glass row" role="status" data-testid="terrace-status" data-state={surface.status}
          style={{ position: "fixed", left: "50%", transform: "translateX(-50%)", bottom: "calc(var(--safe-bottom, 0px) + 116px)", zIndex: 25, maxWidth: "calc(100vw - 32px)", padding: "9px 13px", gap: 10, flexWrap: "wrap", fontSize: 13 }}>
          <span>{t(surface.status === "loading" ? "base.surfaceLoading" : "base.surfaceUnavailable")}</span>
          {surface.status === "error" ? <Button variant="ghost" data-testid="terrace-retry" onClick={() => {
            const terrace = handle?.scene.getObjectByName("enclave.terrace");
            if (terrace) retryTerrace(terrace);
          }}>{t("store.retry")}</Button> : null}
        </div>
      ) : null}
      <div className="chips base-control-strip" data-testid="base-chips">
        <ConstructionSummary />
        <div className="base-quick-stats">
        <button type="button" className="chip" title={t("progression.total")} onClick={() => useBase.getState().setPanel({ kind: "hq" })} data-testid="chip-enclave-power">
          <Icon name="arrowUp" size={14} /> {base.enclave.power.toLocaleString()}
        </button>
        <button type="button" className={`chip ${base.power.deficit ? "warn" : ""}`} onClick={() => useBase.getState().setPanel({ kind: "hq" })} data-testid="chip-power">
          <Icon name="info" size={14} /> {base.power.produced}/{base.power.consumed}
        </button>
        <button type="button" className={`chip ${base.gate.ok ? "ok" : ""}`} onClick={() => useBase.getState().setPanel({ kind: "hq" })} data-testid="chip-gate">
          <Icon name="list" size={14} /> {base.gate.average.toFixed(1)}/{base.gate.required}
        </button>
        <button type="button" className="chip" onClick={() => useBase.getState().setPanel({ kind: "research" })} data-testid="chip-research">
          <Icon name="search" size={14} /> {base.queues.research.used}/{base.queues.research.slots}
        </button>
        <button type="button" className="chip" onClick={() => useBase.getState().setPanel({ kind: "zones" })} data-testid="chip-zones">
          <Icon name="expand" size={14} /> {base.zones.filter((z) => z.state === "open").length}/{base.zones.length}
        </button>
        </div>
      </div>

      {mode.kind === "view" ? (
        <button type="button" className="fab deco-fab" onClick={() => useBase.getState().setPanel(panel.kind === "decorations" ? { kind: "none" } : { kind: "decorations" })} data-testid="deco-fab" aria-label={t("base.decorate.title")}>
          <Icon name="edit" size={22} />
          {decoOwned > 0 ? <span className="fab-badge" data-testid="deco-fab-badge">{decoOwned}</span> : null}
        </button>
      ) : null}
      {mode.kind === "view" ? (
        <button type="button" className={`fab ${readyNew ? "pulse" : ""}`} onClick={() => useBase.getState().setPanel(panel.kind === "build" ? { kind: "none" } : { kind: "build" })} data-testid="build-fab" aria-label={readyCount > 0 ? `${t("base.build.title")} · ${t("base.build.readyCount", { n: readyCount })}` : t("base.build.title")}>
          <Icon name="plus" size={25} />
          {readyCount > 0 ? <span className="fab-badge" data-testid="build-fab-badge" data-new={readyNew || undefined}>{readyCount}</span> : null}
        </button>
      ) : (
        <div className="glass placement-bar" data-testid="placement-bar" data-x={ghost?.x} data-y={ghost?.y} data-rotation={mode.rot}>
          <span className="grow" style={{ minWidth: 0 }}>
            <strong className="ellipsis">{modeName}</strong>
            <div className="hint">{ghost?.valid ? t("base.place.valid") : t("base.place.invalid")}</div>
          </span>
          <Button onClick={rotate} disabled={placing} aria-label={t("base.rotate")} style={{ minHeight: 40 }} data-testid="place-rotate">
            <Icon name="rotate" size={18} />
          </Button>
          <Button onClick={() => useBase.getState().cancelMode()} disabled={placing} style={{ minHeight: 40 }} data-testid="place-cancel">
            {t("common.cancel")}
          </Button>
          <Button variant="primary" disabled={!ghost?.valid || placing} aria-busy={placing} style={{ minHeight: 40 }} onClick={() => void useBase.getState().confirmPlacement()} data-testid="place-confirm">
            <Icon name="check" size={18} /> {t("base.place.confirm")}
          </Button>
        </div>
      )}

      {panel.kind === "building" ? <BuildingPanel rowId={panel.rowId} /> : null}
      {panel.kind === "build" ? <BuildMenu /> : null}
      {panel.kind === "decorations" ? <DecorationSheet /> : null}
      {panel.kind === "decoration" ? <PlacedDecorationSheet id={panel.id} /> : null}
      {panel.kind === "hq" ? <HqPanel /> : null}
      {panel.kind === "queue" ? <QueuePanel /> : null}
      {panel.kind === "zones" ? (
        <ZonePanel
          onFocus={(zoneId) => {
            const z = base.zones.find((x) => x.id === zoneId);
            if (z) sceneRef.current?.focusCell(z.rect.x, z.rect.y, z.rect.w, z.rect.h);
          }}
        />
      ) : null}
      {panel.kind === "protocols" ? <ProtocolPanel /> : null}
      {panel.kind === "research" ? <ResearchPanel /> : null}
      {panel.kind === "layouts" ? <LayoutPanel /> : null}
    </>
  );
}

/** A tapped decoration: what it is, then Move or Store (D70). */
function PlacedDecorationSheet({ id }: { id: string }) {
  const t = useT();
  const d = useBase((s) => s.base?.loop?.decorations.find((x) => x.id === id));
  const setPanel = useBase((s) => s.setPanel);
  const startMoveDecoration = useBase((s) => s.startMoveDecoration);
  const removeDecoration = useBase((s) => s.removeDecoration);
  if (!d) return null;
  const def = decorationById(d.decoId);
  return (
    <div className="glass placement-bar" data-testid="decoration-actions">
      <span className="grow" style={{ minWidth: 0 }}>
        <strong className="ellipsis">{pick(def.name)}</strong>
        <div className="hint">{t("base.decorate.morale", { n: def.morale })} · {d.x},{d.y}</div>
      </span>
      <Button style={{ minHeight: 40 }} onClick={() => startMoveDecoration(d.id)} data-testid="decoration-move"><Icon name="move" size={18} /> {t("base.decorate.move")}</Button>
      <Button style={{ minHeight: 40 }} onClick={() => void removeDecoration(d.id).then(() => setPanel({ kind: "none" }))} data-testid="decoration-store"><Icon name="inventory" size={18} /> {d.decoId === "deco.garden" ? t("colony.removeGarden") : t("base.decorate.remove")}</Button>
      <Button variant="ghost" style={{ minHeight: 40 }} onClick={() => setPanel({ kind: "none" })} data-testid="decoration-close">{t("common.close")}</Button>
    </div>
  );
}
